import type { FlatFamilyResponse, ObjectApartmentSummary, WholeFlatConflict } from '@/shared/api';
import { formatDateRange } from '@/shared/lib/format';

export type FlatFamilyRole = 'wholeFlat' | 'room';

/** Für einen Benutzer, der das andere Mitglied nicht sehen darf. */
export const HIDDEN_FAMILY_LABEL = 'Gehört zu einem Wohnungsverbund';

/** Die Rolle nennt der Server immer, auch wenn das andere Apartment verborgen ist. */
export function flatFamilyRole(
  family: FlatFamilyResponse | null | undefined,
): FlatFamilyRole | null {
  if (!family) return null;
  return family.role === 'Room' ? 'room' : 'wholeFlat';
}

/** Im Verbund, aber jedes andere Mitglied ist für diesen Benutzer verborgen (§6.63). */
export function isHiddenFamily(family: FlatFamilyResponse | null | undefined): boolean {
  return !!family && !family.wholeFlat && family.rooms.length === 0;
}

/** Kurzform für den Kopf der Apartmentakte. */
export function flatFamilyBadgeLabel(family: FlatFamilyResponse | null | undefined): string | null {
  const role = flatFamilyRole(family);
  if (role === null) return null;
  if (isHiddenFamily(family)) return HIDDEN_FAMILY_LABEL;
  if (role === 'room') return `Zimmer von ${family!.wholeFlat!.adminDisplayName}`;
  return `Gesamtwohnung · ${family!.rooms.length} Zimmer`;
}

const label = (apartment: ObjectApartmentSummary) => apartment.adminDisplayName ?? apartment.name;

const byLabel = (left: ObjectApartmentSummary, right: ObjectApartmentSummary) =>
  label(left).localeCompare(label(right), 'de');

const hasRooms = (apartments: ObjectApartmentSummary[], id: string) =>
  apartments.some((apartment) => apartment.wholeFlatPropertyId === id);

/**
 * Was der Server als Gesamtwohnung annähme: ein anderes, nicht archiviertes Apartment desselben
 * Objekts, das nicht selbst Zimmer ist. Die Prüfung bleibt beim Server; das hier ordnet nur die Auswahl.
 */
export function wholeFlatOptions(apartments: ObjectApartmentSummary[], roomId: string) {
  return apartments
    .filter((apartment) => apartment.id !== roomId && apartment.status !== 'Archived')
    .filter((apartment) => !apartment.wholeFlatPropertyId)
    .sort(byLabel);
}

/** Apartments, die als Zimmer dazukommen können: frei, nicht archiviert, selbst ohne Zimmer. */
export function roomOptions(apartments: ObjectApartmentSummary[], wholeFlatId: string) {
  return apartments
    .filter((apartment) => apartment.id !== wholeFlatId && apartment.status !== 'Archived')
    .filter((apartment) => !apartment.wholeFlatPropertyId && !hasRooms(apartments, apartment.id))
    .sort(byLabel);
}

export interface NestedApartment {
  apartment: ObjectApartmentSummary;
  /** Zimmer stehen eingerückt unter ihrer Gesamtwohnung. */
  isRoom: boolean;
}

/** Gesamtwohnung oben, ihre Zimmer direkt darunter; sonst bleibt die Reihenfolge des Servers. */
export function nestFlatFamilies(apartments: ObjectApartmentSummary[]): NestedApartment[] {
  const visibleIds = new Set(apartments.map((apartment) => apartment.id));
  const isNestedRoom = (apartment: ObjectApartmentSummary) =>
    !!apartment.wholeFlatPropertyId && visibleIds.has(apartment.wholeFlatPropertyId);
  return apartments
    .filter((apartment) => !isNestedRoom(apartment))
    .flatMap((apartment) => [
      { apartment, isRoom: false },
      ...apartments
        .filter((room) => room.wholeFlatPropertyId === apartment.id)
        .map((room) => ({ apartment: room, isRoom: true })),
    ]);
}

/** Erste Nacht bis Abreisetag, wie Buchungen sonst auch gezeigt werden. */
export function conflictPeriod(conflict: WholeFlatConflict): string {
  return formatDateRange(conflict.from, conflict.to);
}
